import { useCallback, useEffect, useState } from "react";

import { fetchMeetings, type Meeting } from "@/lib/api";

interface MeetingsState {
  meetings: Meeting[];
  loading: boolean;
  error: string | null;
}

/**
 * Loads `GET /api/meetings` (the signed-in user's series) on mount. Mirrors `useTopics` —
 * returns the list plus loading/error flags and a `reload()` for manual refresh (e.g. after
 * creating a meeting or starting an occurrence).
 */
export function useMeetings() {
  const [state, setState] = useState<MeetingsState>({
    meetings: [],
    loading: true,
    error: null,
  });

  const load = useCallback((signal?: AbortSignal, options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    // Silent (interval) loads must not flip `loading` — Dashboard gates the whole meetings
    // grid on it and would flash every card to a skeleton every 60s. They also must not
    // blank a visible error mid-cycle before the fetch resolves.
    if (!silent) {
      setState((s) => ({ ...s, loading: true, error: null }));
    }
    fetchMeetings(signal)
      .then((meetings) => {
        if (signal?.aborted) return;
        setState({ meetings, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (signal?.aborted || (err as Error)?.name === "AbortError") return;
        if (silent) {
          // Keep the existing list/error visible on a transient background-refresh failure —
          // don't wipe the dashboard over a blip.
          setState((s) => ({ ...s, loading: false }));
          return;
        }
        setState({
          meetings: [],
          loading: false,
          error: (err as Error)?.message ?? "Failed to load meetings",
        });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Modest background refresh (Phase C) so "Next: <run>" / "🔴 Live now" stay current without
  // a websocket — silent: no loading flag, no skeleton flash, no error blanking.
  useEffect(() => {
    const id = setInterval(() => load(undefined, { silent: true }), 60_000);
    return () => clearInterval(id);
  }, [load]);

  const reload = useCallback(() => load(), [load]);

  return { ...state, reload };
}
